<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">
      <div class="webhooks-grid">

        <!-- ── Webhooks Panel ────────────────────────────────────────── -->
        <Panel>
          <template #header>
            <span class="panel-title">WEBHOOKS</span>
            <div class="header-actions">
              <span class="panel-meta">{{ webhooks.length }} registered</span>
              <button v-if="isOwner" class="action-btn" @click="openAddWebhook">+ ADD WEBHOOK</button>
            </div>
          </template>
          <div v-if="webhooks.length === 0" class="empty-row">
            No webhooks registered. Add one to receive push notifications when jobs complete.
          </div>
          <div v-for="w in webhooks" :key="w.id" class="list-row">
            <div class="row-left">
              <div class="webhook-info">
                <span class="webhook-name">{{ w.name }}</span>
                <span class="webhook-url">{{ truncateUrl(w.url) }}</span>
              </div>
            </div>
            <div class="row-right">
              <span class="status-badge" :class="w.is_active ? 'active' : 'inactive'">
                {{ w.is_active ? 'ACTIVE' : 'INACTIVE' }}
              </span>
              <!-- Inline test result -->
              <span v-if="testResults[w.id]" class="test-result" :class="testResults[w.id].ok ? 'ok' : 'fail'">
                {{ testResults[w.id].ok ? `✓ ${testResults[w.id].code}` : `✗ ${testResults[w.id].label}` }}
              </span>
              <button
                v-if="isOwner && w.is_active"
                class="test-btn"
                :disabled="testing[w.id]"
                @click="sendTestPing(w)"
              >{{ testing[w.id] ? '…' : 'TEST' }}</button>
              <span class="date-label">{{ formatDate(w.created_at) }}</span>
              <button v-if="isOwner" class="remove-btn" @click="confirmDelete(w)">✕</button>
            </div>
          </div>
        </Panel>

        <!-- ── Recent Deliveries Panel ───────────────────────────────── -->
        <Panel>
          <template #header>
            <span class="panel-title">RECENT DELIVERIES</span>
            <span class="panel-meta">last 30 days</span>
          </template>
          <div v-if="deliveries.length === 0" class="empty-row">
            No deliveries yet — deliveries will appear here after your first pipeline run.
          </div>
          <template v-else>
            <div
              v-for="d in deliveries"
              :key="d.id"
              class="delivery-row"
              @click="toggleExpand(d.id)"
            >
              <div class="delivery-main">
                <div class="delivery-left">
                  <span class="delivery-event">{{ d.event }}</span>
                  <span class="delivery-attempt" v-if="d.attempt > 1">attempt {{ d.attempt }}</span>
                </div>
                <div class="delivery-right">
                  <span class="delivery-code" v-if="d.status_code">{{ d.status_code }}</span>
                  <span class="delivery-status" :class="d.status">{{ d.status.toUpperCase() }}</span>
                  <span class="delivery-date">{{ formatDateTime(d.delivered_at) }}</span>
                  <span class="expand-icon">{{ expanded.has(d.id) ? '▲' : '▼' }}</span>
                </div>
              </div>
              <div v-if="expanded.has(d.id)" class="delivery-detail" @click.stop>
                <div v-if="d.error_message" class="detail-section">
                  <span class="detail-label">ERROR</span>
                  <pre class="detail-code">{{ d.error_message }}</pre>
                </div>
                <div class="detail-section">
                  <span class="detail-label">PAYLOAD</span>
                  <pre class="detail-code">{{ formatJson(d.payload) }}</pre>
                </div>
              </div>
            </div>
          </template>
        </Panel>

      </div>
    </main>

    <!-- ── Add Webhook Modal ─────────────────────────────────────────── -->
    <div v-if="addOpen && !newSecret" class="modal-backdrop" @click.self="addOpen = false">
      <div class="modal">
        <div class="modal-header">
          <span class="modal-title">ADD WEBHOOK</span>
          <button class="modal-close" @click="addOpen = false">✕</button>
        </div>
        <div class="modal-body">
          <label class="field-label">NAME</label>
          <input v-model="addForm.name" class="field-input" type="text" placeholder="e.g. Production server" />
          <label class="field-label" style="margin-top:14px">URL</label>
          <input v-model="addForm.url" class="field-input" type="url" placeholder="https://example.com/webhooks" />
          <p v-if="addError" class="field-error">{{ addError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="addOpen = false">CANCEL</button>
          <button class="btn-primary" :disabled="addSaving || !addForm.name.trim() || !addForm.url.trim()" @click="saveWebhook">
            {{ addSaving ? 'SAVING…' : 'ADD WEBHOOK' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Secret Reveal Modal ───────────────────────────────────────── -->
    <div v-if="newSecret" class="modal-backdrop">
      <div class="modal modal-wide">
        <div class="modal-header">
          <span class="modal-title">COPY YOUR SIGNING SECRET</span>
        </div>
        <div class="modal-body">
          <p class="key-warning">⚠ Copy this secret now — it will never be shown again.</p>
          <div class="key-reveal">
            <span class="key-reveal-text">{{ newSecret }}</span>
            <button class="copy-btn" @click="copySecret">{{ secretCopied ? 'COPIED ✓' : 'COPY' }}</button>
          </div>

          <div class="sig-section">
            <button class="sig-toggle" @click="sigOpen = !sigOpen">
              {{ sigOpen ? '▲' : '▼' }} How to verify signatures
            </button>
            <div v-if="sigOpen" class="sig-body">
              <p class="sig-hint">
                SmartPrompt signs every delivery with an HMAC-SHA256 of the raw request body using your secret.
                Compare <code>X-SmartPrompt-Signature</code> using a constant-time comparison.
              </p>
              <p class="sig-lang">Node.js</p>
              <pre class="sig-code">const sig = crypto.createHmac('sha256', secret)
  .update(rawBody).digest('hex')
// compare: 'sha256=' + sig === req.headers['x-smartprompt-signature']</pre>
              <p class="sig-lang">Ruby</p>
              <pre class="sig-code">sig = OpenSSL::HMAC.hexdigest('SHA256', secret, request.raw_post)
# compare: 'sha256=' + sig == request.headers['X-SmartPrompt-Signature']</pre>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary" @click="closeSecretModal">DONE</button>
        </div>
      </div>
    </div>

    <!-- ── Delete Confirm Modal ──────────────────────────────────────── -->
    <div v-if="deleteTarget" class="modal-backdrop" @click.self="deleteTarget = null">
      <div class="modal modal-sm">
        <div class="modal-header">
          <span class="modal-title">DELETE WEBHOOK</span>
          <button class="modal-close" @click="deleteTarget = null">✕</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">
            Delete webhook <strong>{{ deleteTarget.name }}</strong>?
            No further deliveries will be sent to this URL.
          </p>
          <p v-if="deleteError" class="field-error">{{ deleteError }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">CANCEL</button>
          <button class="btn-danger" :disabled="deleting" @click="deleteWebhook">
            {{ deleting ? 'DELETING…' : 'DELETE' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import Panel from "../../components/Panel.vue"

const props = defineProps({
  project:    { type: Object, required: true },
  webhooks:   { type: Array,  default: () => [] },
  deliveries: { type: Array,  default: () => [] },
  user_role:  { type: String, default: "viewer" },
})

const isOwner = computed(() => props.user_role === "owner")

// ── helpers ──────────────────────────────────────────────────────────────────
function truncateUrl(url) {
  return url.length > 60 ? url.slice(0, 57) + "…" : url
}
function formatDate(dateStr) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })
}
function formatDateTime(dateStr) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
}
function formatJson(obj) {
  try { return JSON.stringify(obj, null, 2) } catch { return String(obj) }
}
async function apiCall(method, path, body = null) {
  const token = document.querySelector("meta[name='csrf-token']")?.content
  const opts = { method, headers: { "Content-Type": "application/json", "X-CSRF-Token": token } }
  if (body) opts.body = JSON.stringify(body)
  const res = await fetch(path, opts)
  return { status: res.status, data: await res.json() }
}
function reload() {
  router.visit(`/projects/${props.project.uuid}/webhooks`, { replace: true })
}

// ── Test ping ─────────────────────────────────────────────────────────────────
const testing     = ref({})   // { [webhookId]: true }
const testResults = ref({})   // { [webhookId]: { ok, code, label } }

async function sendTestPing(webhook) {
  testing.value = { ...testing.value, [webhook.id]: true }
  testResults.value = { ...testResults.value, [webhook.id]: null }
  try {
    const { status, data } = await apiCall("POST", `/api/v1/projects/${props.project.uuid}/webhooks/${webhook.id}/test`)
    if (status === 200) {
      testResults.value = {
        ...testResults.value,
        [webhook.id]: {
          ok:    data.delivered,
          code:  data.status_code,
          label: data.error || (data.status_code ? `${data.status_code}` : "Error")
        }
      }
    } else {
      testResults.value = { ...testResults.value, [webhook.id]: { ok: false, label: "Request failed" } }
    }
  } catch {
    testResults.value = { ...testResults.value, [webhook.id]: { ok: false, label: "Network error" } }
  } finally {
    testing.value = { ...testing.value, [webhook.id]: false }
  }
}

// ── Expanded deliveries ───────────────────────────────────────────────────────
const expanded = ref(new Set())
function toggleExpand(id) {
  const s = new Set(expanded.value)
  if (s.has(id)) { s.delete(id) } else { s.add(id) }
  expanded.value = s
}

// ── Add Webhook ───────────────────────────────────────────────────────────────
const addOpen     = ref(false)
const addSaving   = ref(false)
const addError    = ref("")
const addForm     = ref({ name: "", url: "" })
const newSecret   = ref("")
const secretCopied = ref(false)
const sigOpen     = ref(false)

function openAddWebhook() {
  addForm.value = { name: "", url: "" }
  addError.value = ""
  newSecret.value = ""
  secretCopied.value = false
  addOpen.value = true
}
async function saveWebhook() {
  addSaving.value = true
  addError.value  = ""
  try {
    const { status, data } = await apiCall("POST", `/api/v1/projects/${props.project.uuid}/webhooks`, addForm.value)
    if (status === 201) {
      newSecret.value = data?.webhook?.secret || ""
      addOpen.value = false
    } else {
      addError.value = data?.message || "Failed to create webhook."
    }
  } catch { addError.value = "Network error." }
  finally { addSaving.value = false }
}
async function copySecret() {
  try { await navigator.clipboard.writeText(newSecret.value); secretCopied.value = true } catch {}
}
function closeSecretModal() {
  newSecret.value = ""
  reload()
}

// ── Delete Webhook ────────────────────────────────────────────────────────────
const deleteTarget = ref(null)
const deleting     = ref(false)
const deleteError  = ref("")

function confirmDelete(webhook) {
  deleteTarget.value = webhook
  deleteError.value  = ""
}
async function deleteWebhook() {
  deleting.value    = true
  deleteError.value = ""
  try {
    const { status, data } = await apiCall("DELETE", `/api/v1/projects/${props.project.uuid}/webhooks/${deleteTarget.value.id}`)
    if (status === 200) { deleteTarget.value = null; reload() }
    else deleteError.value = data?.message || "Failed to delete."
  } catch { deleteError.value = "Network error." }
  finally { deleting.value = false }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}
.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}
.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
}
.webhooks-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* ── Panel header ── */
.panel-title {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(232, 230, 227, 0.45);
}
.panel-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.action-btn {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 3px 9px;
  border-radius: 3px;
  border: 1px solid rgba(245, 166, 35, 0.35);
  background: rgba(245, 166, 35, 0.08);
  color: #f5a623;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.action-btn:hover {
  background: rgba(245, 166, 35, 0.16);
  border-color: rgba(245, 166, 35, 0.6);
}

/* ── Empty ── */
.empty-row {
  padding: 24px 18px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.25);
  text-align: center;
  line-height: 1.6;
}

/* ── Webhook list ── */
.list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  gap: 12px;
  transition: background 0.15s;
}
.list-row:hover { background: rgba(245, 166, 35, 0.04); }
.row-left { display: flex; align-items: center; flex: 1; min-width: 0; }
.row-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.webhook-info { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.webhook-name {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  color: #e8e6e3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.webhook-url {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.status-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 2px 7px;
  border-radius: 3px;
}
.status-badge.active  { background: rgba(74, 222, 128, 0.15); color: #4ade80; }
.status-badge.inactive { background: rgba(255, 255, 255, 0.06); color: rgba(232, 230, 227, 0.35); }
.date-label { font-family: var(--font-mono); font-size: 10px; color: rgba(232, 230, 227, 0.25); }
.remove-btn {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 3px;
  border: 1px solid transparent;
  background: transparent;
  color: rgba(248, 113, 113, 0.4);
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}
.remove-btn:hover { color: #f87171; border-color: rgba(248, 113, 113, 0.35); background: rgba(248, 113, 113, 0.08); }
.test-btn {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 2px 9px;
  border-radius: 3px;
  border: 1px solid rgba(245, 166, 35, 0.3);
  background: rgba(245, 166, 35, 0.06);
  color: rgba(245, 166, 35, 0.7);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.test-btn:hover:not(:disabled) { background: rgba(245, 166, 35, 0.14); color: #f5a623; }
.test-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.test-result {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 3px;
}
.test-result.ok   { background: rgba(74, 222, 128, 0.12); color: #4ade80; }
.test-result.fail { background: rgba(248, 113, 113, 0.12); color: #f87171; }

/* ── Delivery rows ── */
.delivery-row {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background 0.15s;
}
.delivery-row:hover { background: rgba(245, 166, 35, 0.03); }
.delivery-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  gap: 12px;
}
.delivery-left { display: flex; align-items: center; gap: 8px; }
.delivery-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.delivery-event {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  color: #e8e6e3;
}
.delivery-attempt {
  font-family: var(--font-mono);
  font-size: 9px;
  color: rgba(245, 166, 35, 0.7);
}
.delivery-code {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.45);
}
.delivery-status {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 3px;
}
.delivery-status.delivered { background: rgba(74, 222, 128, 0.15); color: #4ade80; }
.delivery-status.failed    { background: rgba(248, 113, 113, 0.15); color: #f87171; }
.delivery-status.pending   { background: rgba(255, 255, 255, 0.06); color: rgba(232, 230, 227, 0.45); }
.delivery-date {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}
.expand-icon {
  font-size: 8px;
  color: rgba(232, 230, 227, 0.25);
}
.delivery-detail {
  padding: 0 18px 14px;
  cursor: default;
}
.detail-section { margin-bottom: 10px; }
.detail-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(232, 230, 227, 0.25);
  display: block;
  margin-bottom: 4px;
}
.detail-code {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  padding: 10px 12px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.6);
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
}

/* ── Modal ── */
.modal-backdrop {
  position: fixed; inset: 0; z-index: 50;
  background: rgba(0, 0, 0, 0.72);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.modal {
  background: #141416;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 100%;
  max-width: 460px;
  display: flex;
  flex-direction: column;
}
.modal.modal-sm  { max-width: 360px; }
.modal.modal-wide { max-width: 560px; }
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.modal-title {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 1.2px; color: rgba(232, 230, 227, 0.6);
}
.modal-close {
  background: none; border: none; color: rgba(232, 230, 227, 0.3);
  font-size: 14px; cursor: pointer; padding: 0; line-height: 1;
  transition: color 0.15s;
}
.modal-close:hover { color: #e8e6e3; }
.modal-body { padding: 20px; display: flex; flex-direction: column; }
.modal-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex; justify-content: flex-end; gap: 10px;
}
.field-label {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1.2px; color: rgba(232, 230, 227, 0.35);
  margin-bottom: 6px; display: block;
}
.field-input {
  width: 100%; box-sizing: border-box;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  padding: 9px 12px;
  font-family: var(--font-mono); font-size: 12px; color: #e8e6e3;
  outline: none; transition: border-color 0.15s;
}
.field-input:focus { border-color: rgba(245, 166, 35, 0.5); }
.field-error {
  font-family: var(--font-mono); font-size: 10px; color: #f87171; margin-top: 8px;
}
.confirm-text {
  font-family: var(--font-body); font-size: 13px;
  color: rgba(232, 230, 227, 0.7); line-height: 1.5;
}
.confirm-text strong { color: #e8e6e3; }

/* ── Secret reveal ── */
.key-warning {
  font-family: var(--font-mono); font-size: 11px;
  color: #f5a623; margin-bottom: 14px;
}
.key-reveal {
  display: flex; align-items: stretch;
  border: 1px solid rgba(245, 166, 35, 0.3); border-radius: 5px; overflow: hidden;
}
.key-reveal-text {
  flex: 1; padding: 10px 12px;
  font-family: var(--font-mono); font-size: 11px;
  color: #e8e6e3; word-break: break-all;
  background: rgba(245, 166, 35, 0.05);
}
.copy-btn {
  padding: 10px 14px;
  background: rgba(245, 166, 35, 0.15); border: none;
  border-left: 1px solid rgba(245, 166, 35, 0.3);
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  letter-spacing: 0.6px; color: #f5a623; cursor: pointer;
  transition: background 0.15s; white-space: nowrap;
}
.copy-btn:hover { background: rgba(245, 166, 35, 0.25); }

/* ── Signature hint ── */
.sig-section { margin-top: 20px; }
.sig-toggle {
  font-family: var(--font-mono); font-size: 10px; font-weight: 600;
  color: rgba(232, 230, 227, 0.4); background: none; border: none;
  cursor: pointer; padding: 0; transition: color 0.15s;
}
.sig-toggle:hover { color: rgba(232, 230, 227, 0.7); }
.sig-body { margin-top: 12px; }
.sig-hint {
  font-family: var(--font-mono); font-size: 10px;
  color: rgba(232, 230, 227, 0.35); line-height: 1.6; margin: 0 0 12px;
}
.sig-hint code {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(232, 230, 227, 0.5);
  padding: 1px 5px; border-radius: 3px;
}
.sig-lang {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.8px; color: rgba(232, 230, 227, 0.25);
  margin: 0 0 4px;
}
.sig-code {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  padding: 10px 12px;
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.55);
  white-space: pre;
  overflow-x: auto;
  margin: 0 0 12px;
}

/* ── Buttons ── */
.btn-primary {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px; border: none;
  background: #f5a623; color: #0a0a0b; cursor: pointer;
  transition: opacity 0.15s;
}
.btn-primary:hover:not(:disabled) { opacity: 0.88; }
.btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-ghost {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent; color: rgba(232, 230, 227, 0.45); cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}
.btn-ghost:hover { border-color: rgba(255, 255, 255, 0.2); color: rgba(232, 230, 227, 0.7); }
.btn-danger {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 18px; border-radius: 5px;
  background: rgba(248, 113, 113, 0.15); color: #f87171; cursor: pointer;
  border: 1px solid rgba(248, 113, 113, 0.3);
  transition: background 0.15s;
}
.btn-danger:hover:not(:disabled) { background: rgba(248, 113, 113, 0.25); }
.btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
